(() => {
  const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
  const FINE_POINTER = '(hover: hover) and (pointer: fine)';

  class HomeApple extends HTMLElement {
    constructor() {
      super();
      this.abortController = new AbortController();
      this.observers = [];
      this.animationFrames = new Map();
      this.reducedMotion = window.matchMedia(REDUCED_MOTION).matches;
    }

    connectedCallback() {
      this.setupReveals();
      this.setupRails();
      this.setupVideos();
      this.setupHero();
      this.setupDepthCards();
    }

    disconnectedCallback() {
      this.abortController.abort();
      this.observers.forEach((observer) => observer.disconnect());
      this.animationFrames.forEach((frame) => cancelAnimationFrame(frame));
      this.animationFrames.clear();
    }

    setupReveals() {
      const elements = [...this.querySelectorAll('[data-reveal]')];
      if (!elements.length) return;

      if (this.reducedMotion || !('IntersectionObserver' in window)) {
        elements.forEach((element) => element.classList.add('is-in-view'));
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in-view');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
      );

      elements.forEach((element) => observer.observe(element));
      this.observers.push(observer);
    }

    setupRails() {
      this.querySelectorAll('[data-drag-rail]').forEach((rail) => this.makeRailDraggable(rail));

      this.querySelectorAll('[data-rail-prev], [data-rail-next]').forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            const section = button.closest('section');
            const rail = section?.querySelector('[data-product-rail], [data-video-rail]');
            if (!rail) return;

            const points = this.getSnapPoints(rail);
            const currentIndex = this.closestPointIndex(points, rail.scrollLeft);
            const direction = button.hasAttribute('data-rail-next') ? 1 : -1;
            const target = points[Math.max(0, Math.min(points.length - 1, currentIndex + direction))];
            this.springRail(rail, target, 0, true);
          },
          { signal: this.abortController.signal }
        );
      });
    }

    makeRailDraggable(rail) {
      const state = {
        pointerId: null,
        startX: 0,
        startY: 0,
        startScroll: 0,
        dragging: false,
        moved: false,
        history: [],
      };

      const stopAnimation = () => {
        const frame = this.animationFrames.get(rail);
        if (frame) cancelAnimationFrame(frame);
        this.animationFrames.delete(rail);
      };

      rail.addEventListener(
        'pointerdown',
        (event) => {
          if (event.button !== 0 && event.pointerType === 'mouse') return;
          if (event.pointerType !== 'mouse') return;
          if (event.target.closest('a, button, input, select, textarea, summary, [role="button"]')) return;
          stopAnimation();
          state.pointerId = event.pointerId;
          state.startX = event.clientX;
          state.startY = event.clientY;
          state.startScroll = rail.scrollLeft;
          state.dragging = false;
          state.moved = false;
          state.history = [{ x: event.clientX, time: performance.now() }];
          rail.setPointerCapture?.(event.pointerId);
        },
        { signal: this.abortController.signal }
      );

      rail.addEventListener(
        'pointermove',
        (event) => {
          if (event.pointerId !== state.pointerId) return;
          const deltaX = event.clientX - state.startX;
          const deltaY = event.clientY - state.startY;

          if (!state.dragging) {
            if (Math.hypot(deltaX, deltaY) < 10) return;
            if (Math.abs(deltaY) > Math.abs(deltaX)) {
              state.pointerId = null;
              rail.releasePointerCapture?.(event.pointerId);
              return;
            }
            state.dragging = true;
            state.moved = true;
            rail.classList.add('is-dragging');
          }

          event.preventDefault();
          const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
          const requested = state.startScroll - deltaX;
          let next = requested;

          if (requested < 0) next = -this.rubberBand(-requested, rail.clientWidth);
          if (requested > maxScroll) {
            next = maxScroll + this.rubberBand(requested - maxScroll, rail.clientWidth);
          }

          rail.scrollLeft = next;
          const now = performance.now();
          state.history.push({ x: event.clientX, time: now });
          state.history = state.history.filter((sample) => now - sample.time <= 120).slice(-6);
        },
        { signal: this.abortController.signal }
      );

      const finishDrag = (event) => {
        if (event.pointerId !== state.pointerId) return;
        rail.releasePointerCapture?.(event.pointerId);
        state.pointerId = null;
        rail.classList.remove('is-dragging');

        if (!state.dragging) return;
        state.dragging = false;

        const last = state.history[state.history.length - 1];
        const first = state.history[0] || last;
        const duration = Math.max(1, last.time - first.time);
        const pointerVelocity = ((last.x - first.x) / duration) * 1000;
        const scrollVelocity = -pointerVelocity;
        const deceleration = 0.998;
        const rawProjection = (scrollVelocity / 1000) * (deceleration / (1 - deceleration));
        const projectionLimit = rail.clientWidth * 2.2;
        const projection = Math.max(-projectionLimit, Math.min(projectionLimit, rawProjection));
        const points = this.getSnapPoints(rail);
        const projectedPosition = rail.scrollLeft + projection;
        const target = points[this.closestPointIndex(points, projectedPosition)] || 0;
        this.springRail(rail, target, scrollVelocity, false);

        window.setTimeout(() => {
          state.moved = false;
        }, 0);
      };

      rail.addEventListener('pointerup', finishDrag, { signal: this.abortController.signal });
      rail.addEventListener('pointercancel', finishDrag, { signal: this.abortController.signal });
      rail.addEventListener(
        'click',
        (event) => {
          if (!state.moved) return;
          event.preventDefault();
          event.stopPropagation();
        },
        { capture: true, signal: this.abortController.signal }
      );
    }

    rubberBand(distance, dimension) {
      const constant = 0.55;
      return (distance * constant * dimension) / (dimension + constant * distance);
    }

    getSnapPoints(rail) {
      const railBox = rail.getBoundingClientRect();
      const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const points = [...rail.children]
        .filter((child) => child instanceof HTMLElement)
        .map((child) => child.getBoundingClientRect().left - railBox.left + rail.scrollLeft)
        .map((point) => Math.max(0, Math.min(maxScroll, point)));
      return [...new Set([0, ...points, maxScroll].map((point) => Math.round(point)))].sort((a, b) => a - b);
    }

    closestPointIndex(points, value) {
      return points.reduce(
        (bestIndex, point, index) =>
          Math.abs(point - value) < Math.abs(points[bestIndex] - value) ? index : bestIndex,
        0
      );
    }

    springRail(rail, target, initialVelocity = 0, direct = false) {
      const previousFrame = this.animationFrames.get(rail);
      if (previousFrame) cancelAnimationFrame(previousFrame);

      const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const destination = Math.max(0, Math.min(maxScroll, target));
      if (this.reducedMotion) {
        rail.scrollLeft = destination;
        return;
      }

      let position = rail.scrollLeft;
      let velocity = initialVelocity;
      let lastTime = performance.now();
      const response = direct ? 0.35 : 0.4;
      const dampingRatio = direct ? 1 : 0.82;
      const stiffness = Math.pow((2 * Math.PI) / response, 2);
      const damping = 2 * dampingRatio * Math.sqrt(stiffness);

      const step = (now) => {
        const deltaTime = Math.min(0.032, Math.max(0.001, (now - lastTime) / 1000));
        lastTime = now;
        const acceleration = stiffness * (destination - position) - damping * velocity;
        velocity += acceleration * deltaTime;
        position += velocity * deltaTime;
        rail.scrollLeft = position;

        if (Math.abs(destination - position) < 0.45 && Math.abs(velocity) < 4) {
          rail.scrollLeft = destination;
          this.animationFrames.delete(rail);
          return;
        }

        const frame = requestAnimationFrame(step);
        this.animationFrames.set(rail, frame);
      };

      const frame = requestAnimationFrame(step);
      this.animationFrames.set(rail, frame);
    }

    setupVideos() {
      const videos = [...this.querySelectorAll('[data-ugc-video]')];
      if (!videos.length) return;

      const pauseVideo = (video) => {
        video.pause();
        const button = video.parentElement?.querySelector('[data-video-toggle]');
        if (!button) return;
        button.setAttribute('aria-pressed', 'false');
        button.setAttribute('aria-label', 'Videoyu oynat');
        const icon = button.querySelector('span');
        if (icon) icon.textContent = '▶';
      };

      const loadSources = (video) => {
        const pending = video.querySelectorAll('source[data-src]');
        if (!pending.length) return;
        pending.forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        video.load();
      };

      videos.forEach((video) => {
        const button = video.parentElement?.querySelector('[data-video-toggle]');
        if (!button) return;

        button.addEventListener(
          'click',
          async () => {
            if (!video.paused) {
              pauseVideo(video);
              return;
            }

            videos.filter((item) => item !== video).forEach(pauseVideo);
            loadSources(video);
            try {
              await video.play();
              button.setAttribute('aria-pressed', 'true');
              button.setAttribute('aria-label', 'Videoyu duraklat');
              const icon = button.querySelector('span');
              if (icon) icon.textContent = 'Ⅱ';
            } catch (_error) {
              pauseVideo(video);
            }
          },
          { signal: this.abortController.signal }
        );
      });

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => entries.forEach((entry) => !entry.isIntersecting && pauseVideo(entry.target)),
          { threshold: 0.18 }
        );
        videos.forEach((video) => observer.observe(video));
        this.observers.push(observer);
      }
    }

    setupHero() {
      const hero = this.querySelector('.home-apple__hero');
      const media = this.querySelector('[data-hero-media]');
      const video = this.querySelector('[data-hero-video]');
      if (!hero || !media) return;

      if (video && !this.reducedMotion) {
        video.play().catch(() => {});
        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver(
            ([entry]) => {
              if (entry.isIntersecting) video.play().catch(() => {});
              else video.pause();
            },
            { threshold: 0.18 }
          );
          observer.observe(hero);
          this.observers.push(observer);
        }
      }

      if (this.reducedMotion || !window.matchMedia(FINE_POINTER).matches) return;
      let pointerX = 0;
      let pointerY = 0;
      let scrollY = 0;
      let frame = 0;

      const render = () => {
        media.style.setProperty('--hero-x', `${pointerX * -0.75}px`);
        media.style.setProperty('--hero-y', `${pointerY * -0.75 + scrollY}px`);
        media.style.setProperty('--hero-scale', '1.055');
        frame = 0;
      };

      const requestRender = () => {
        if (!frame) frame = requestAnimationFrame(render);
      };

      hero.addEventListener(
        'pointermove',
        (event) => {
          const box = hero.getBoundingClientRect();
          pointerX = ((event.clientX - box.left) / box.width - 0.5) * 12;
          pointerY = ((event.clientY - box.top) / box.height - 0.5) * 12;
          requestRender();
        },
        { signal: this.abortController.signal }
      );

      hero.addEventListener(
        'pointerleave',
        () => {
          pointerX = 0;
          pointerY = 0;
          requestRender();
        },
        { signal: this.abortController.signal }
      );

      window.addEventListener(
        'scroll',
        () => {
          const box = hero.getBoundingClientRect();
          scrollY = box.bottom > 0 ? Math.max(-12, Math.min(12, -box.top * 0.025)) : 0;
          requestRender();
        },
        { passive: true, signal: this.abortController.signal }
      );
    }

    setupDepthCards() {
      if (this.reducedMotion || !window.matchMedia(FINE_POINTER).matches) return;

      this.querySelectorAll('[data-depth-card]').forEach((card) => {
        card.addEventListener(
          'pointermove',
          (event) => {
            const box = card.getBoundingClientRect();
            const x = (event.clientX - box.left) / box.width - 0.5;
            const y = (event.clientY - box.top) / box.height - 0.5;
            card.style.setProperty('--depth-x', `${x * 3.5}deg`);
            card.style.setProperty('--depth-y', `${y * -3.5}deg`);
          },
          { signal: this.abortController.signal }
        );

        card.addEventListener(
          'pointerleave',
          () => {
            card.style.setProperty('--depth-x', '0deg');
            card.style.setProperty('--depth-y', '0deg');
          },
          { signal: this.abortController.signal }
        );
      });
    }
  }

  if (!customElements.get('home-apple')) {
    customElements.define('home-apple', HomeApple);
  }
})();
